<script setup lang="ts">
  import { computed, defineAsyncComponent, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import AlertQC from '@qicgroupdoha/qic-connect-ds/components/AlertQC'
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import OoredooBanner from '@/widgets/InsuranceSuccessWidget/components/OoredooBanner/OoredooBanner.vue'
  import includes from '@mocks/includes'
  import notIncludes from '@mocks/notIncludes'
  import { documents } from '@/shared/constants/documents.constants'
  import { useVisitorsStore } from '@/shared/store'
  import { useFeaturesStore } from '@/shared/store/feature'
  import { PaymentType } from '@/shared/types/response.types'
  import type { VisitorType } from '@/shared/types/store.types'

  import QuoteCheckout from '@/shared/components/rebranding/quote/checkout/QuoteCheckout.vue'
  import QuoteInclusive from '@/shared/components/rebranding/quote/inclusive/QuoteInclusive.vue'
  import QuoteInsuredList from '@/shared/components/rebranding/quote/insured-list/QuoteInsuredList.vue'

  import { formatHolderFullName } from '../QuoteWidget.helpers'

  const HealthCoverage = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/health-coverage/HealthCoverage.vue')
  )
  const InboundAddon = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/inbound-addon/InboundAddon.vue')
  )
  const CoversPopup = defineAsyncComponent(
    () => import('@/shared/components/rebranding/popups/CoversPopup.vue')
  )

  interface Props {
    visitorsWithDurationMatch: VisitorType[]
    dates: { startDate: string; tillDate: string }
    singleQuotePrice: number
    totalPrice: number
    termsHref: string
    benefitsLink: string
    policyWordingInbound: string
    isShowInboundAddon: boolean
    isInboundAddonAbTest: boolean
    isInboundAddonSelected: boolean
    isLoadingInboundAddon: boolean
    inboundPolicyExists: boolean
    isLoading: boolean
    isPassportExpiredAndBlocked: boolean
    passportExpiredAlertText: string
    availablePaymentMethods: PaymentType[]
    getApplePayTotalCallback: () => number
    validateBeforePayment: () => Promise<boolean>
  }

  defineProps<Props>()

  const emit = defineEmits<{
    (e: 'add'): void
    (e: 'edit', visitor: VisitorType): void
    (e: 'delete', visitor: VisitorType): void
    (e: 'expand', visitor: VisitorType): void
    (e: 'open-remove-window-event', visitor: VisitorType): void
    (e: 'close-remove-window-event', visitor: VisitorType): void
    (e: 'inbound-toggle'): void
    (e: 'on-payment', payload: { paymentMethod: number; isGpayOrApplePay: boolean }): void
    (e: 'learn-more', name: string): void
    (e: 'error', value: string): void
    (e: 'close-passport-alert'): void
  }>()

  const visitorsStore = useVisitorsStore()
  const featuresStore = useFeaturesStore()
  const { locale } = useI18n()

  const isCoversPopupShown = ref(false)

  const visitors = computed(() => visitorsStore.visitorsList)
  const quoteNumber = computed(() => visitorsStore.getQuoteData?.quote || '')
  const inboundPrice = computed(() => visitorsStore.inboundPrice || 0)
  const isOoredooEnabled = computed(() => featuresStore.isOoredooEnabled)
  const holderFullName = computed(() => formatHolderFullName(visitorsStore.holder))
</script>

<template>
  <FlowCard :title="$t('qlm_quote_header')" :with-button="false" class="quote-form flow">
    <div class="quote-dates text-medium">
      <div class="quote-dates__inner">
        {{ $t('policy_date') }} {{ dates.startDate }} - {{ dates.tillDate }}
      </div>
    </div>

    <div class="quote">
      <QuoteInsuredList
        :visitors="visitors"
        @add="emit('add')"
        @edit="emit('edit', $event)"
        @delete="emit('delete', $event)"
        @expand="emit('expand', $event)"
        @open-remove-window-event="emit('open-remove-window-event', $event)"
        @close-remove-window-event="emit('close-remove-window-event', $event)"
      />
    </div>
  </FlowCard>

  <HealthCoverage
    v-if="isShowInboundAddon"
    :price="singleQuotePrice"
    :is-one-person="visitorsWithDurationMatch.length === 1"
    :policy-wording="termsHref"
    :quote-number="quoteNumber"
    :benefits-link="benefitsLink"
    @on-learn-more-click="emit('learn-more', 'health_coverage')"
  />

  <InboundAddon
    v-if="isShowInboundAddon"
    :price="inboundPrice"
    :is-loading="isLoadingInboundAddon"
    :is-selected="isInboundAddonSelected"
    :is-one-person="visitorsWithDurationMatch.length === 1"
    :is-warning="inboundPolicyExists"
    :full-name="holderFullName"
    :policy-wording="policyWordingInbound"
    :quote-number="quoteNumber"
    @on-select="emit('inbound-toggle')"
    @on-learn-more-click="emit('learn-more', 'inbound_coverage')"
  />

  <FlowCard v-if="!isShowInboundAddon" :with-button="false" class="quote-form flow">
    <QuoteInclusive
      :includes="includes"
      :quote-id="quoteNumber"
      @show:covers="isCoversPopupShown = true"
    />
  </FlowCard>

  <OoredooBanner
    v-if="isOoredooEnabled"
    :policy-id="quoteNumber"
    :with-button="false"
    :title="$t('ooredoo_block.quote.header')"
    :descriptions="[
      $t('ooredoo_block.quote.description_1'),
      $t('ooredoo_block.quote.description_2'),
      $t('ooredoo_block.quote.description_3'),
    ]"
    :wrap-title="false"
  />

  <FlowCard :with-button="false" class="quote-form flow quote-form--payment">
    <h3 class="heading-6 payment-title">{{ $t('qic_quote_payment_select') }}</h3>
    <QuoteCheckout
      :person-count="visitorsWithDurationMatch.length"
      :quote-inclusive-price="singleQuotePrice"
      :quote-number="quoteNumber"
      :quote-total-price="totalPrice"
      :is-inbound-addon="isInboundAddonAbTest"
      :is-inbound-addon-selected="isInboundAddonSelected"
      :is-loading="isLoading"
      :loading-price="isLoadingInboundAddon"
      :inbound-addon-price="inboundPrice"
      :terms-href="termsHref"
      :available-payment-methods="availablePaymentMethods"
      :get-apple-pay-total-callback="getApplePayTotalCallback"
      :validate-before-payment="validateBeforePayment"
      @on-payment="emit('on-payment', $event)"
      @error="emit('error', $event)"
    />
  </FlowCard>

  <!-- @vue-ignore -->
  <ModalQC
    v-if="isCoversPopupShown"
    :title="$t('covers_popup.title')"
    hide-controls
    position="side"
    :locale="locale"
    @close="isCoversPopupShown = false"
  >
    <template #content>
      <CoversPopup :includes="includes" :not-includes="notIncludes" :documents="documents" />
    </template>
  </ModalQC>

  <AlertQC
    v-if="isPassportExpiredAndBlocked"
    class="alert-passport-expired"
    :subtitle="passportExpiredAlertText"
    variant="error"
    closable
    @close="emit('close-passport-alert')"
  />
</template>

<style lang="scss" scoped>
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';

  .quote-form {
    gap: var(--spacing-4);
  }

  .quote-dates {
    margin: 0 auto;
  }

  .quote-dates__inner {
    color: var(--colorTxtSrfPrimary);
    white-space: nowrap;
  }

  .payment-title {
    padding-block: var(--spacing-2);
    margin-bottom: var(--spacing-3);
  }

  .quote-form--payment {
    gap: var(--spacing-5);
  }

  .alert-passport-expired {
    position: fixed;
    top: var(--spacing-9);
    inset-inline-end: var(--spacing-8);
    max-width: 366px;
    z-index: 1000;
    :deep(.ds-alert__content) {
      .ds-alert__text-subtitle {
        @include typography.text-medium;
      }
    }
    @media (max-width: 568px) {
      top: var(--spacing-4);
      inset-inline: var(--spacing-4);
      max-width: 100%;
    }
  }
</style>
